import React from 'react'

import { F0Box, F0Button } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { Delete, Handle } from '@factorialco/f0-react/icons/app'
import {
  AtsApplicationPhaseListItemFragment,
  AtsEditHiringPipelineApplicationPhaseDocument,
  AtsGetHiringPipelinePhasesDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  applicationPhase: AtsApplicationPhaseListItemFragment
  onDelete: () => void
}

export const ApplicationPhaseListItem = ({ applicationPhase, onDelete }: Props) => {
  const [name, setName] = React.useState(applicationPhase.name)

  React.useEffect(() => {
    setName(applicationPhase.name)
  }, [applicationPhase.name])

  const [updatePhase] = useMutation(AtsEditHiringPipelineApplicationPhaseDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsApplicationPhase,
    refetchQueries: [AtsGetHiringPipelinePhasesDocument],
  })

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed || trimmed === applicationPhase.name) {
      setName(applicationPhase.name)
      return
    }

    const result = await updatePhase({
      variables: {
        id: applicationPhase.id,
        name: trimmed,
        atsHiringStageId: applicationPhase.atsHiringStageId,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      setName(applicationPhase.name)
    }
  }

  return (
    <F0Box display="flex" flexDirection="row" alignItems="center" gap="md" width="full">
      <F0Box grow>
        <div onPointerDown={(e) => e.stopPropagation()}>
          <Input
            name={`phase-${applicationPhase.id}`}
            label={i18n.t(
              'ats.job_posting_settings.hiring_pipeline.edit_phase_modal.phase_name_label'
            )}
            hideLabel
            value={name}
            onChange={setName}
            onBlur={handleSave}
            onPressEnter={handleSave}
            size="sm"
          />
        </div>
      </F0Box>

      <F0Box display="flex" flexDirection="row" alignItems="center" gap="xs" shrink={false}>
        <span
          aria-label={i18n.t('common.drag_to_reorder')}
          style={{
            cursor: 'grab',
            touchAction: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
          }}
        >
          <Handle width={10} height={14} />
        </span>
        <div onPointerDown={(e) => e.stopPropagation()}>
          <F0Button
            variant="ghost"
            size="md"
            icon={Delete}
            label={i18n.t('common.delete')}
            hideLabel
            onClick={onDelete}
          />
        </div>
      </F0Box>
    </F0Box>
  )
}
